import React from 'react';
import { ProjectGraphData, Project } from '@/types/project';
import { formatCompactCurrency, formatCompactNumber, formatDate } from '@/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  TrendingUp, 
  TrendingDown, 
  ArrowUpRight,
  DollarSign, 
  BarChart3,
  FileText
} from 'lucide-react';
import ProjectChart from './ProjectChart';
import FundingRoundDropdown from './FundingRoundDropdown';

interface ProjectAnalyticsProps {
  graphData: ProjectGraphData | null;
  projectId: string;
  project: Project | null;
  loading?: boolean;
}

// Helper function to extract enum value (e.g., "BUY" from "OrderType.BUY")
const extractEnumValue = (value: string): string => {
  if (!value) return value;
  // If it contains a dot, extract the part after the dot
  if (value.includes('.')) {
    return value.split('.').pop() || value;
  }
  return value;
};

// Helper function to format deal type for display
const formatDealType = (dealType: string): string => {
  const value = extractEnumValue(dealType);
  // Convert to readable format (e.g., "LIQUID_TOKEN" -> "Liquid Token", "VESTING" -> "Vesting")
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const ProjectAnalytics: React.FC<ProjectAnalyticsProps> = ({ graphData, project, loading = false }) => {
  if (!graphData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-400">No analytics data available</p>
      </div>
    );
  }

  // Calculate summary statistics
  const totalOrders = graphData.orders?.length || 0;
  const totalFundingRounds = graphData.funding_rounds?.length || 0;

  const totalOrderValue = graphData.orders?.reduce((sum, order) => sum + order.offered_amount, 0) || 0;
  const totalFundingRaised = graphData.funding_rounds?.reduce((sum, round) => sum + (round.amount_raised || 0), 0) || 0;

  const latestPrice = graphData.price_history?.[graphData.price_history.length - 1];
  const firstPrice = graphData.price_history?.[0];

  const priceChange = latestPrice && firstPrice 
    ? ((latestPrice.price_usd - firstPrice.price_usd) / firstPrice.price_usd) * 100
    : null;

  // Prepare stats for display - 8 cards in 2 rows
  const statsRow1 = [
    {
      label: 'Total Orders',
      value: formatCompactNumber(totalOrders),
      icon: <BarChart3 size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
      withMutedDollar: false,
      isPriceChange: false,
    },
    {
      label: 'Total Order Value',
      value: formatCompactCurrency(totalOrderValue),
      icon: <DollarSign size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
      withMutedDollar: true,
      isPriceChange: false,
    },
    {
      label: 'Funding Rounds',
      value: formatCompactNumber(totalFundingRounds),
      icon: <TrendingUp size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
      withMutedDollar: false,
      isPriceChange: false,
    },
    {
      label: 'Total Funding Raised',
      value: formatCompactCurrency(totalFundingRaised),
      icon: <ArrowUpRight size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
      withMutedDollar: true,
      isPriceChange: false,
    },
  ];

  const statsRow2 = [
    {
      label: 'Current Price',
      value: latestPrice ? `$${latestPrice.price_usd.toFixed(2)}` : '—',
      icon: <DollarSign size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
      withMutedDollar: true,
      isPriceChange: false,
    },
    {
      label: 'Market Cap',
      value: latestPrice ? formatCompactCurrency(latestPrice.market_cap_usd) : '—',
      icon: <BarChart3 size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
      withMutedDollar: true,
      isPriceChange: false,
    },
    {
      label: 'FDV',
      value: latestPrice ? formatCompactCurrency(latestPrice.spot_fdv_usd) : '—',
      icon: <FileText size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
      withMutedDollar: true,
      isPriceChange: false,
    },
    {
      label: 'Price Change',
      value: priceChange !== null ? `${priceChange > 0 ? '+' : ''}${priceChange.toFixed(2)}%` : '—',
      icon: priceChange !== null && priceChange > 0 
        ? <TrendingUp size={16} className="text-[#10b981]" />
        : priceChange !== null && priceChange < 0 
          ? <TrendingDown size={16} className="text-[#ff540c]" />
          : <TrendingUp size={16} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]" />,
      isNegative: priceChange !== null && priceChange < 0,
      withMutedDollar: false,
      isPriceChange: true,
    },
  ];

  const renderCurrencyValue = (value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue.startsWith('$')) {
      return trimmedValue;
    }

    return (
      <>
        <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span>{' '}
        {trimmedValue.slice(1)}
      </>
    );
  };

  const renderPriceChangeValue = (value: string) => {
    if (value === '—') {
      return value;
    }

    const isNegative = value.startsWith('-');
    const isPositive = value.startsWith('+');
    const numericPart = value.replace('%', '').replace('+', '').replace('-', '');
    const sign = isNegative ? '-' : isPositive ? '+' : '';
    const colorClass = isNegative ? 'text-[#ff540c]' : 'text-[#1fb845]';

    return (
      <>
        <span className={colorClass}>{sign}{numericPart}</span>
        <span className={`text-[14px] leading-[32px] ${colorClass}`}>%</span>
      </>
    );
  };

  const renderStatValue = (value: string, withMutedDollar: boolean, isPriceChange: boolean) => {
    if (isPriceChange) {
      return renderPriceChangeValue(value);
    }
    if (withMutedDollar) {
      return renderCurrencyValue(value);
    }
    return value;
  };

  return (
    <div className="space-y-7">
      {/* Project Valuation Chart */}
      <div>
        <h2 className="font-['Plus_Jakarta_Sans'] font-semibold text-[20px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px] mb-5">
          Project Valuation Chart
        </h2>
        <ProjectChart graphData={graphData} loading={loading} />
      </div>

      {/* Analytics Overview */}
      <div className="bg-[rgba(255,255,255,0.32)] dark:bg-[rgba(23,23,23,0.72)] border border-white dark:border-[#171717] px-8 pt-8 pb-8">
        <h2 className="font-['Plus_Jakarta_Sans'] font-semibold text-[20px] leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd] mb-7">
          Analytics Overview
        </h2>
        
        {loading ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="bg-white dark:bg-[#1c1c1c] h-[124px] px-6 py-6">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-6 w-16" />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="bg-white dark:bg-[#1c1c1c] h-[124px] px-6 py-6">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-6 w-16" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Row 1 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {statsRow1.map((stat, index) => (
                <div key={index} className="bg-white dark:bg-[#1c1c1c] h-[124px] px-6 py-6 flex flex-col justify-between">
                  <div className="flex items-center gap-[6px]">
                    {stat.icon}
                    <span className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                      {stat.label}
                    </span>
                  </div>
                  <p className="font-['Plus_Jakarta_Sans'] font-medium text-[23px] leading-[32px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                    {renderStatValue(stat.value, stat.withMutedDollar, stat.isPriceChange)}
                  </p>
                </div>
              ))}
            </div>
            {/* Row 2 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {statsRow2.map((stat, index) => (
                <div key={index} className="bg-white dark:bg-[#1c1c1c] h-[124px] px-6 py-6 flex flex-col justify-between">
                  <div className="flex items-center gap-[6px]">
                    {stat.icon}
                    <span className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                      {stat.label}
                    </span>
                  </div>
                  <p className={`font-['Plus_Jakarta_Sans'] font-medium text-[23px] leading-[32px] tracking-[-0.2px] ${
                    stat.isNegative ? 'text-[#ff540c]' : 'text-[#2d2923] dark:text-[#fdfdfd]'
                  }`}>
                    {renderStatValue(stat.value, stat.withMutedDollar, stat.isPriceChange)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Funding History & Recent Orders - 2 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_520px] gap-4">
        {/* Funding History */}
        <div className="bg-[rgba(255,255,255,0.32)] dark:bg-[rgba(23,23,23,0.72)] border border-white dark:border-[#171717] px-8 pt-8 pb-8">
          <h2 className="font-['Plus_Jakarta_Sans'] font-semibold text-[20px] leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd] mb-7">
            Funding History
          </h2>
          
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="bg-white dark:bg-[#1c1c1c] border border-[rgba(45,41,35,0.12)] p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Skeleton className="w-10 h-10" />
                      <div>
                        <Skeleton className="h-4 w-28 mb-2" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                    <Skeleton className="h-5 w-16" />
                  </div>
                </div>
              ))}
            </div>
          ) : project?.rounds && project.rounds.length > 0 ? (
            <div className="space-y-3">
              {project.rounds.map((round, index) => (
                <FundingRoundDropdown key={index} round={round} index={index} />
              ))}
            </div>
          ) : (
            <p className="font-['Plus_Jakarta_Sans'] text-[13px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
              No funding history available
            </p>
          )}
        </div>

        {/* Recent Orders */}
        <div className="bg-[rgba(255,255,255,0.32)] dark:bg-[rgba(23,23,23,0.72)] border border-white dark:border-[#171717] px-8 pt-8 pb-8 lg:min-h-[927px]">
          <h2 className="font-['Plus_Jakarta_Sans'] font-semibold text-[20px] leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd] mb-7">
            Recent Orders
          </h2>
          
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="bg-white dark:bg-[#1c1c1c] border border-[rgba(45,41,35,0.12)] px-5 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Skeleton className="w-16 h-6" />
                    <div>
                      <Skeleton className="h-4 w-16 mb-1" />
                      <Skeleton className="h-3 w-24" />
                    </div>
                  </div>
                  <Skeleton className="h-5 w-16" />
                </div>
              ))}
            </div>
          ) : graphData.orders && graphData.orders.length > 0 ? (
            <div className="space-y-3">
              {graphData.orders.slice(0, 10).map((order, index) => {
                const orderType = extractEnumValue(order.type);
                const dealType = extractEnumValue(order.deal_type);
                const isBuy = orderType.includes('BUY') || orderType === 'BUY';
                
                return (
                  <div 
                    key={index} 
                    className="bg-white dark:bg-[#1c1c1c] border border-[rgba(45,41,35,0.12)] px-5 py-4 flex items-center justify-between"
                  >
                    <div className="flex items-center w-[240px]">
                      <div className="flex flex-col gap-[6px]">
                        <div className="flex items-start gap-[14px]">
                          <div className={`min-w-[76px] h-6 px-2 rounded-none bg-transparent flex items-center justify-center border text-[12px] leading-[16px] font-['Plus_Jakarta_Sans'] font-semibold ${
                            isBuy 
                              ? 'border-[#1fb845] text-[#1fb845]' 
                              : 'border-[#ff540c] text-[#ff540c]'
                          }`}>
                            {isBuy ? 'BUY' : 'SELL'}
                          </div>
                          <div className="min-w-[44px] h-6 px-2 rounded-none bg-transparent flex items-center justify-center">
                            <span className="font-['Plus_Jakarta_Sans'] font-semibold text-[12px] leading-[16px] text-[#2d2923] dark:text-[#fdfdfd]">
                              {formatDealType(dealType)}
                            </span>
                          </div>
                        </div>
                        <p className="font-['Plus_Jakarta_Sans'] font-normal text-[12px] leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                          {formatDate(order.date)} • {order.round || 'Unknown'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-['Plus_Jakarta_Sans'] font-medium text-[18px] leading-[24px] tracking-[-0.2px] text-[#2d2923] dark:text-[#fdfdfd]">
                        {renderCurrencyValue(formatCompactCurrency(order.offered_amount))}
                      </p>
                      <p className="font-['Plus_Jakarta_Sans'] font-normal text-[12px] leading-[16px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                        FDV: {formatCompactCurrency(order.offered_fully_diluted_valuation)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="font-['Plus_Jakarta_Sans'] text-[13px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
              No orders available
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectAnalytics; 
